import { useEffect } from 'react'
import s from './EmailSettings.scss'
import { INTEGRATIONS } from 'utils/constants'
import { KeyValuePairs, InputContainer, Checkbox } from 'components/ideal-core-ui'
import { useImmer } from 'use-immer'
import Select from 'react-select'
import { Button } from 'reactstrap'
import { refreshToken } from 'utils/authService'

const EmailSettings = ({ saveDetails, details }) => {
  const [organizationDetails, setOrganizationDetails] = useImmer(details)
  const { emailSettings, emailIntegration, emailTC } = organizationDetails

  useEffect(() => {
    setOrganizationDetails(() => {
      return details
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details])

  useEffect(() => {
    if (organizationDetails.shouldSave) {
      saveDetails(organizationDetails, refreshToken)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationDetails])

  return (
    <div className={s.wrapper}>
      <h5>Outgoing Email Server Settings</h5>
      <p className={s.subtitle}>
        Email server settings allow you to select how you want email communications from the iDeal
        system to be sent out.
      </p>
      {emailIntegration !== INTEGRATIONS.MAIL.NON_PAYING && (
        <>
          <input
            type="radio"
            checked={emailIntegration === INTEGRATIONS.MAIL.IDEAL || false}
            onChange={() => {
              setOrganizationDetails(draft => {
                draft.emailIntegration = INTEGRATIONS.MAIL.IDEAL
                draft.shouldSave = true
              })
            }}
            data-cy="ideal_email_checkbox"
          />{' '}
          iDeal Mail
          <p className={s.description}>
            If you choose iDeal Mail, emails will go out through proposals@idealcrm.app, with ‘From’
            and ‘Reply To’ set to your email address. So the recipients will still see that the
            emails are coming from you. This selection works well for those users who don’t want to
            go through connecting their own email to the system. The down-side here is that you
            won’t see sent emails in your Inbox’ Sent folder. Uou’ll only see them on the ‘Sent
            Emails’ tab on the main navigation in iDeal.
          </p>
        </>
      )}
      <input
        type="radio"
        checked={emailIntegration === INTEGRATIONS.MAIL.SMTP || false}
        onChange={() => {
          setOrganizationDetails(draft => {
            draft.emailIntegration = INTEGRATIONS.MAIL.SMTP
            draft.shouldSave = false
          })
        }}
        data-cy="smtp_email_checkbox"
      />{' '}
      Connect via SMTP - Set Up SMTP Server
      <p className={s.description}>
        If you choose to connect via SMTP, you will set up your company’s SMTP server below.
        Additionally, each user will see a form to connect their own email in their User Profile.
        Once these are set up, all email communicatios from iDeal will go out from each user’s email
        and each user will be able to go to their company email Sent folder and see the emails that
        went out. The SMTP connection is a secure option as we only connect to your email to send
        out email communications. We don’t hold any read rights
      </p>
      {emailIntegration === INTEGRATIONS.MAIL.SMTP && (
        <div className={s.smtpWrapper}>
          <KeyValuePairs
            pairs={[
              {
                key: '* Name',
                value: (
                  <InputContainer
                    type="text"
                    placeholder="Enter Name i.e. Yahoo, Comcast, Zoho, yMail"
                    cb={value =>
                      setOrganizationDetails(draft => {
                        draft.emailSettings.name = value
                      })
                    }
                    text={emailSettings?.name || ''}
                    cy="email_server_name_input"
                  />
                )
              },
              {
                key: '* Email Server Address',
                value: (
                  <InputContainer
                    type="text"
                    placeholder="smtp.domain"
                    cb={value =>
                      setOrganizationDetails(draft => {
                        draft.emailSettings.domain = value
                      })
                    }
                    text={emailSettings?.domain || ''}
                    cy="email_server_address_input"
                  />
                )
              },
              {
                key: '* Email Server Port',
                value: (
                  <InputContainer
                    type="number"
                    placeholder="Enter Port Number i.e. 587, 465,..."
                    cb={value =>
                      setOrganizationDetails(draft => {
                        draft.emailSettings.port = value
                      })
                    }
                    text={emailSettings?.port || ''}
                    cy="email_server_port_input"
                  />
                )
              },
              {
                key: '* Connection Security',
                value: (
                  <Select
                    placeholder="Select One"
                    value={emailSettings?.mode}
                    onChange={value =>
                      setOrganizationDetails(draft => {
                        draft.emailSettings.mode = value
                      })
                    }
                    options={INTEGRATIONS.CONNECTION_MODE}
                    id="email_server_security_cy"
                  />
                )
              }
            ]}
          />
          <Button
            disabled={
              !emailSettings?.name ||
              !emailSettings?.domain ||
              !emailSettings?.mode ||
              !emailSettings?.port
            }
            className={s.saveBtn}
            onClick={() => saveDetails(organizationDetails, refreshToken)}
            data-cy="submit_smtp_btn"
          >
            Save SMTP Server Settings
          </Button>
        </div>
      )}
      <input
        type="radio"
        checked={emailIntegration === INTEGRATIONS.MAIL.OFFICE365 || false}
        onChange={() => {
          setOrganizationDetails(draft => {
            draft.emailIntegration = INTEGRATIONS.MAIL.OFFICE365
            draft.shouldSave = true
          })
        }}
        data-cy="outlook_email_checkbox"
      />{' '}
      Connect Outlook 365
      <p className={s.description}>
        If you choose to connect via Outlook 365, each user will see a form to connect their own
        email in their User Profile. Once these are set up, all email communicatios from iDeal will
        go out from each user’s email and each user will be able to go to their company email Sent
        folder and see the emails that went out. The SMTP connection is a secure option as we only
        connect to your email to send out email communications. We don’t hold any read rights.
      </p>
      <h5 style={{ marginRight: '15px', display: 'inline-block', fontSize: '18px' }}>
        Turn on &quot;Sent from iDeal CRM&quot; text at the end of all emails.
      </h5>
      <Checkbox
        size="md"
        isChecked={emailSettings.includeDefaultEmailFooter}
        triggerCheckbox={value => {
          setOrganizationDetails(draft => {
            draft.emailSettings.includeDefaultEmailFooter = value.target.checked
            draft.shouldSave = true
          })
        }}
      />
      <div className={s.settingsItem}>
        <h5 style={{ marginRight: '15px', display: 'inline-block', fontSize: '18px' }}>
          Add Email Terms at the end of every email sent out from iDeal CRM
        </h5>
        <Checkbox
          size="md"
          isChecked={emailTC?.active}
          triggerCheckbox={value => {
            setOrganizationDetails(draft => {
              draft.emailTC.active = value.target.checked
              draft.shouldSave = true
            })
          }}
        />
        <InputContainer
          type="textarea"
          rows="3"
          placeholder="Enter Email Terms"
          cb={value =>
            setOrganizationDetails(draft => {
              draft.emailTC.content = value
            })
          }
          text={emailTC?.content || ''}
        />
        <Button className={s.saveBtn} onClick={() => saveDetails(organizationDetails)}>
          Save Text
        </Button>
      </div>
    </div>
  )
}

export default EmailSettings
